import CustomScrollbar from 'components/Common/CustomScrollbar';
import useSnapshotFunctions from 'components/Dashboard/DashboardComponent/useSnapshotFunctions';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import { copyInnerHtml } from 'components/Dashboard/helpers/copyFunctionality';
import currentDate from 'components/Dashboard/utilities/currentDateUTC';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useAdaptiveDateDisplay from 'components/Dashboard/utilities/useAdaptiveDateDisplay';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import RefineBriefingModal from 'components/Dashboard/DashboardComponent/RefineBriefingModal';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import FirstBriefingMessage from 'components/Dashboard/DashboardComponent/FirstBriefingMessage';
import BriefingItem from 'components/Dashboard/DashboardComponent/BriefingItem';
import useDashboardInitialController from 'components/Dashboard/SearchControl/useDashboardInitialController';
import time from 'utils/time';

const SnapshotComponent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, showUpdateDescriptionModal } = state;
  const { role } = team?.myself ?? {};

  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [briefing, setBriefing] = useState({ title: `Daily briefing for ${team?.teamName}`, date: null });
  const [allowedDates, setAllowedDates] = useState([]);
  const [briefingGenerating, setBriefingGenerating] = useState(false);
  const { updateState, resetEventParameter, date: dateProps } = props;
  const date = dateProps ?? currentDate;
  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: dayjs(dateProps ?? briefing?.date).toDate(),
    name: '',
    range: { from: '', to: '' },
  });

  const { listBriefings, getBriefingsByDate, checkBriefing } = useSnapshotFunctions();
  const { showUpdateDescriptionPopup } = useDashboardInitialController();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const briefingContainer = useRef(null);
  const scrollbarRef = useRef(null);
  const subnavContainerRef = useRef(null);
  const subnavTitleRef = useRef(null);
  const subnavActionsRef = useRef(null);

  const getDates = (array) => {
    return array?.map((item) => item?.date)?.sort((a, b) => dayjs(a).diff(dayjs(b)));
  };

  const initialCall = useRef(null);
  initialCall.current = async ({ source, filters = [], newCall }) => {
    try {
      if (!newCall) {
        setIsLoading(true);
      }
      if (!dateProps) {
        const { generationInProgress } = (await checkBriefing({ source })) ?? {};
        if (generationInProgress) {
          setBriefingGenerating(true);
          setIsLoading(false);
          return;
        }
      }
      let { results, mostRecent } = await getBriefingsByDate({ source, filters, date });
      if ((!mostRecent || results?.length === 0) && !dateProps) {
        const requestProperties = { sort: { field: 'date', order: 'desc' } };
        //OP: Get last briefing date from listBriefings
        let { mostRecent: listMostRecent } = await listBriefings({
          source,
          filters,
          requestProperties,
        });
        const listDate = listMostRecent?.[0]?.date?.split('T')[0];
        //OP: Call actual briefing data with the last briefing date
        ({ results, mostRecent } = await getBriefingsByDate({ source, filters, date: listDate }));
      }
      let content = !!mostRecent ? [...mostRecent] : [];
      if (!!results) {
        setResults(results);
        setBriefing({
          ...briefing,
          title: `Daily briefing for ${team?.teamName}`,
          content: content,
          date: dayjs(mostRecent?.[0]?.date)?.format('YYYY-MM-DD'),
        });
        setBriefingGenerating(false);
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current({ source });
    return () => {
      source.abort('Snapshots loading cancelled by the user');
    };
  }, [team?.teamId, dateProps]);

  useEffect(() => {
    if (!briefingGenerating) {
      return;
    }
    const source = new AbortController();
    let pollInFlight = false;
    const id = setInterval(async () => {
      if (pollInFlight) {
        return;
      }
      pollInFlight = true;
      try {
        await initialCall.current({ source, newCall: true });
      } finally {
        pollInFlight = false;
      }
    }, 10000);
    return () => {
      clearInterval(id);
      source.abort('Briefing generation check cancelled by the user.');
    };
  }, [briefingGenerating, team?.teamId, dateProps]);

  useEffect(() => {
    if (!!briefing || dateProps) {
      setCalendarState((prev) => ({
        ...prev,
        selectedDay: dayjs(dateProps ?? briefing?.date).toDate(),
      }));
    }
  }, [briefing, dateProps]);

  useEffect(() => {
    if (scrollbarRef.current) {
      scrollbarRef.current?.scrollTop();
    }
  }, [team?.teamId, dateProps, scrollbarRef.current]);

  useEffect(() => {
    const changingSelectFromElements = (allow) => {
      const selectValue = allow ? 'auto' : 'none';
      const topNav = document.querySelector('.main-header');
      if (topNav) {
        topNav.style.userSelect = selectValue;
      }
    };
    changingSelectFromElements();
    return () => {
      changingSelectFromElements(true);
    };
  }, []);

  const goToLatestBriefingFunction = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({ pathname: `/dashboard`, search: location?.search });
  };

  const handleMonthChange = async (e, source) => {
    try {
      const filters = {
        filters: [
          { field: 'date', operator: 'date_gte', value: dayjs(e).startOf('month').format('YYYY-MM-DD') },
          { field: 'date', operator: 'date_lt', value: dayjs(e).add(1, 'month').startOf('month').format('YYYY-MM-DD') },
        ],
      };
      const requestProperties = { ...filters, pageSize: 1000 };
      let results;
      if (!allowedDates[dayjs(e).startOf('month').format('YYYY-MM')] && !isLoading) {
        ({ results } = await listBriefings({ requestProperties, source }));
        if (!!results) {
          setAllowedDates({ ...allowedDates, [dayjs(e).startOf('month').format('YYYY-MM')]: getDates(results) });
        }
      }
    } catch (error) {}
  };

  const restoreScrollPosition = (clientY, url, innerText) => {
    setTimeout(() => {
      let targetElement = null;
      const elements = scrollbarRef.current?.view?.querySelectorAll(`a[href="${url}"]`) || [];
      targetElement = Array.from(elements).find((el) => el.innerText === innerText);

      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect();
        const viewElement = scrollbarRef.current?.view;
        const viewRect = viewElement?.getBoundingClientRect();
        const currentScroll = viewElement?.scrollTop || 0;

        const scrollValue = currentScroll + (targetRect.top - (viewRect?.top || 0)) - (clientY - (viewRect?.top || 0));
        if (scrollbarRef.current?.view && scrollValue !== undefined) {
          scrollbarRef.current.view.scroll({ top: scrollValue, behavior: 'smooth' });
        }
      }
    }, 200);
  };

  const allowChangeBriefingTopics = role === 'Admin' || role === 'Owner';
  const dateToShow = date ? date : briefing?.date ? dayjs(briefing?.date).format('YYYY-MM-DD') : currentDate;

  const getBriefingContent = (contentSections) => {
    const defaultBriefingContentSections = [
      {
        type: 'drivingtheday',
        subject: 'Driving the day',
        body: '',
        order: 0,
      },
      {
        type: 'incaseyoumissedit',
        subject: 'In case you missed it',
        body: '',
        order: 1,
      },
      {
        type: 'lookingahead',
        subject: 'Looking ahead',
        body: '',
        order: 2,
      },
      {
        type: 'lookingaheadincludingtoday',
        subject: 'Looking ahead including today',
        body: '',
        order: 3,
      },
      {
        type: 'inthemedia',
        subject: 'In the media',
        body: '',
        order: 4,
      },
    ];

    const briefingContent = defaultBriefingContentSections
      .sort((a, b) => a.order - b.order)
      .map((item) =>
        contentSections?.find(
          (section) =>
            section.type?.toLowerCase() === item.type || section?.snapshotItemType?.toLowerCase() === item.type
        )
      )
      .filter((item) => item !== undefined);
    return briefingContent;
  };

  const conditionForFirstBriefing = results?.length === 0 && !isLoading && !dateProps;
  const { newAgencyPlan } = useTopicOrClientWord();

  return (
    <>
      <div className='box-top-rounded h-100 bg-white dashboard-root-container'>
        <div
          ref={subnavContainerRef}
          className={`d-flex flex-column justify-content-center border-bottom specific-x-padding-22 specific-x-padding-lg-30 ${
            conditionForFirstBriefing ? 'specific-y-padding-28 specific-bottom-margin-28' : 'py-3'
          }`}
          style={{ minHeight: isMobile || conditionForFirstBriefing ? null : '90px' }}
        >
          <div className='flex-centered justify-content-between'>
            <div className='overflow-hidden'>
              <h3 className='main-title mb-0 pr-2 line-height-1-4 text-overflow-ellipsis'>
                <span ref={subnavTitleRef} className='font-weight-bold nowrap-item'>
                  {briefing?.title}
                </span>
              </h3>
              {conditionForFirstBriefing && (
                <p className='mt-1 mb-0 light-60-text'>
                  PolicyMogul creates a weekday briefing summarising key political developments relevant to your{' '}
                  {newAgencyPlan ? `${team?.expires ? 'prospect' : 'client'}` : 'work'}. As it’s tailored using{' '}
                  {newAgencyPlan ? 'their' : 'your'} organisation’s description, topics and keywords, please review
                  these before your first briefing is generated.
                </p>
              )}
            </div>
            {!conditionForFirstBriefing && (
              <div className='flex-centered user-select-none'>
                <div className='d-none d-md-block'>
                  <GoToLatestBriefing
                    goToLatestBriefingFunction={goToLatestBriefingFunction}
                    briefing={briefing}
                    results={results}
                    updateState={updateState}
                    isLoading={isLoading}
                    calendarState={calendarState}
                    setCalendarState={setCalendarState}
                    navigationRefs={{
                      containerRef: subnavContainerRef,
                      titleRef: subnavTitleRef,
                      actionsRef: subnavActionsRef,
                    }}
                  />
                </div>
                <div ref={subnavActionsRef} className='flex-centered user-select-none'>
                  <SnapshotShareButton
                    updateState={updateState}
                    briefing={briefing}
                    briefingContainer={briefingContainer}
                  />
                  <SelectBriefingButton
                    results={results}
                    loadingState={{ isLoading, setIsLoading }}
                    briefingState={{ briefing, setBriefing }}
                    briefingContainer={briefingContainer}
                    updateState={updateState}
                    scrollbarRef={scrollbarRef}
                    resetEventParameter={resetEventParameter}
                    allowChangeBriefingTopics={allowChangeBriefingTopics}
                    dateProps={dateProps}
                    handleMonthChange={handleMonthChange}
                    allowedDates={allowedDates}
                    calendarState={calendarState}
                    setCalendarState={setCalendarState}
                  />
                </div>
              </div>
            )}
          </div>
          <GoToLatestBriefing
            goToLatestBriefingFunction={goToLatestBriefingFunction}
            briefing={briefing}
            results={results}
            mobile={true}
            updateState={updateState}
            isLoading={isLoading}
            calendarState={calendarState}
            setCalendarState={setCalendarState}
            navigationRefs={{
              containerRef: subnavContainerRef,
              titleRef: subnavTitleRef,
              actionsRef: subnavActionsRef,
            }}
          />
        </div>

        <div className='light-90-text flex-grow-1'>
          <CustomScrollbar ref={scrollbarRef} className='simple-scrollbar' maximalThumbYSize={100}>
            <div className='specific-left-padding-lg-30 specific-right-padding-lg-20 h-100'>
              {isLoading ? (
                <SkeletonSnapshotComponent />
              ) : briefingGenerating ? (
                <>
                  <div className='h-100 flex-centered justify-content-center'>
                    <div>
                      <div className='text-center pb-5 mx-auto title-h5-m' style={{ marginTop: '-150px' }}>
                        <p className='mb-2 mx-auto'>
                          <strong>Your daily briefing is being generated</strong>
                        </p>
                        <p className='paragraph-p1 mx-auto pt-2' style={{ maxWidth: '300px' }}>
                          <span className='light-50-text'>
                            This could take a minute or two. Feel free to navigate away and come back
                          </span>
                        </p>
                      </div>
                      <div className='waiting-bar mx-auto'>
                        <div className='waiting-bar-indicator' />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {conditionForFirstBriefing ? (
                    <FirstBriefingMessage
                      tailorSummaryState={{
                        showTailorSummaryModal: showUpdateDescriptionModal,
                        setShowTailorSummaryModal: showUpdateDescriptionPopup,
                      }}
                      initialCall={initialCall}
                    />
                  ) : (
                    <>
                      {briefing?.content?.length > 0 ? (
                        <div ref={briefingContainer}>
                          <>
                            {getBriefingContent(briefing?.content)?.map((item, index, array) => {
                              return (
                                <BriefingItem
                                  key={`briefing-${item?.id}`}
                                  item={item}
                                  updateState={updateState}
                                  resetEventParameter={resetEventParameter}
                                  notShowTitle={briefing?.content?.length === 1 && item?.type === 'daily'}
                                  briefingState={{ briefing, setBriefing }}
                                  restoreScrollPosition={restoreScrollPosition}
                                  index={index}
                                  array={array}
                                />
                              );
                            })}
                          </>
                        </div>
                      ) : (
                        <div className='h-100 flex-centered justify-content-center'>
                          <p className='mb-0 light-60-text title-h5-m' style={{ marginTop: '150px' }}>
                            There is no briefing available for{' '}
                            {time(dateToShow).format(
                              `dddd, Do MMMM ${time().isSame(dateToShow, 'year') ? '' : ', YYYY'}`
                            )}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </CustomScrollbar>
        </div>
      </div>
    </>
  );
};

const SkeletonSnapshotComponent = ({ reportItem }) => {
  return (
    <SkeletonTheme baseColor='#f2f2f2'>
      <div className={`py-4  ${reportItem ? '' : 'specific-x-padding-22 specific-x-padding-lg-30'}`}>
        {Array(4)
          .fill()
          .map((item, index) => (
            <div key={index} className='mb-3 w-100'>
              <Skeleton height={19} width={`45%`} />
              <Skeleton count={4} height={19} width={`100%`} />
            </div>
          ))}
      </div>
    </SkeletonTheme>
  );
};

const GoToLatestBriefing = (props) => {
  const {
    goToLatestBriefingFunction,
    mobile,
    updateState,
    briefing,
    isLoading,
    calendarState,
    setCalendarState,
    navigationRefs,
  } = props;
  const { updatePanelState } = updateState ?? {};
  const isOpenPanel = updatePanelState?.open;

  const { date } = useParams();
  const showCloseButton = date && date !== currentDate;
  const dateToShow = date ? date : briefing?.date ? dayjs(briefing?.date).format('YYYY-MM-DD') : currentDate;

  const { buttonRef, selectedFormat, formatsMapping } = useAdaptiveDateDisplay({
    dateToShow,
    mobile,
    containerRef: navigationRefs?.containerRef,
    titleRef: navigationRefs?.titleRef,
    actionsRef: navigationRefs?.actionsRef,
    leftPadding: showCloseButton ? 16 : 32,
    extraDependencies: [showCloseButton, isOpenPanel],
  });

  return (
    <>
      <div
        ref={buttonRef}
        className={`pointer section-filter-button position-relative d-inline-block general-button border rounded-pill border-grey bg-blue-hovered light-90-text ${
          showCloseButton ? 'pl-4' : 'px-4'
        } specific-y-padding-5 specific-right-padding-45 nowrap-item ${
          mobile ? 'mt-2 d-md-none' : `mr-${isOpenPanel ? 2 : 3}`
        } transition-default`}
        onClick={() => setCalendarState({ ...calendarState, active: !calendarState?.active })}
      >
        {isLoading ? (
          <Skeleton height={19} width={mobile ? '45px' : '90px'} />
        ) : (
          time(dateToShow)?.format(formatsMapping[selectedFormat])
        )}
        {showCloseButton && (
          <span
            className='bg-main-blue text-white position-absolute circle-button-26 top-4 right-5 d-flex align-items-center justify-content-center hover-opacity-85 transition-200 pointer'
            onClick={goToLatestBriefingFunction}
          >
            <span className='icon-close paragraph-p5' />
          </span>
        )}
      </div>
    </>
  );
};

const SelectBriefingButton = (props) => {
  const {
    loadingState,
    briefingState,
    briefingContainer,
    updateState,
    scrollbarRef,
    resetEventParameter,
    allowChangeBriefingTopics,
    dateProps,
    handleMonthChange,
    allowedDates,
    calendarState,
    setCalendarState,
  } = props;
  const { setIsLoading } = loadingState ?? {};
  const { briefing } = briefingState ?? {};
  const { setUpdatePanelState } = updateState;

  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const { newAgencyPlan } = useTopicOrClientWord();
  const [isOpen, setIsOpen] = useState(false);
  const [isRefineBriefingsOpen, setIsRefineBriefingsOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const actionsElement = useRef(null);
  const hideFilters = () => {
    setIsOpen(false);
  };
  useOutsideElement(actionsElement, hideFilters);

  const applyCalendarFilter = () => {
    setIsLoading(true);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop();
    }
    resetEventParameter();
    setUpdatePanelState({ open: false, url: null });
    navigate({
      pathname: `/dashboard/${dayjs(calendarState?.selectedDay)?.format('YYYY-MM-DD')}`,
      search: location?.search,
    });
  };

  const isMobile = useIsMobile(576);
  return (
    <>
      <div className='position-relative' ref={actionsElement}>
        <button
          className='general-button circle-button border circle-button-medium flex-centered justify-content-center action-state-hover light-60-text'
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <span className='icon-three-dots d-inline-block-centered centered-1' />
        </button>
        {isOpen && (
          <div
            className='box-shadow pt-2 position-absolute z-extra-max rounded'
            style={{ right: '0px', top: '39px', width: `${isMobile ? '276' : '285'}px` }}
          >
            {allowChangeBriefingTopics && (
              <div
                className='d-flex list-action-button px-3 py-xl-3 py-2 pointer'
                onClick={() => {
                  setIsRefineBriefingsOpen(!isRefineBriefingsOpen);
                }}
              >
                <div className='d-flex flex-centered flex-column icon-general-container specific-right-margin-12'>
                  <span className='icon-filters  d-inline-block specific-top-margin-4 title-h5-m' />
                </div>
                <div>
                  <p className='mb-0 mb-xl-1 font-weight-xl-bold'>Refine future briefings</p>
                  <p className='light-60-text mb-0 d-none d-xl-block specific-left-padding-1'>
                    Update {newAgencyPlan ? `${team?.teamName}'${team?.teamName?.endsWith('s') ? '' : 's'}` : 'your'}{' '}
                    interests and manage which topics are included
                  </p>
                </div>
              </div>
            )}
            <div
              className='d-flex list-action-button px-3 py-xl-3 py-2 pointer'
              onClick={() => setCalendarState({ ...calendarState, active: !calendarState?.active })}
            >
              <div className='d-flex flex-centered flex-column icon-general-container specific-right-margin-12'>
                <span className='icon-clock-thin font-weight-semibold d-inline-block specific-top-margin-4 title-h5-m' />
              </div>
              <div>
                <p className='mb-0 mb-xl-1 font-weight-xl-bold'>See previous briefings</p>
                <p className='light-60-text mb-0 d-none d-xl-block specific-left-padding-1'>
                  {newAgencyPlan
                    ? `View daily briefings for ${team?.teamName} from previous dates`
                    : 'View your daily briefings from previous dates'}
                </p>
              </div>
            </div>
            <div className='d-xl-none'>
              <ShareBriefingActions
                briefing={briefing}
                hideFilters={hideFilters}
                briefingContainer={briefingContainer}
                fromPopup={true}
              />
            </div>
            <div
              className='pointer border-top pl-3 pr-4 pt-2 pb-2 pt-xl-3 pb-xl-4 bg-blue-hovered rounded-bottom d-flex'
              onClick={() => {
                window.Intercom('startSurvey', 56969611);
              }}
            >
              <div className='d-flex flex-centered flex-column icon-general-container specific-right-margin-12'>
                <span className='icon-filters  d-inline-block specific-top-margin-4 title-h5-m' />
              </div>
              <div>
                <p className='mb-0 mb-xl-1 font-weight-xl-bold'>Give feedback on briefings</p>
                <p className='light-60-text mb-0 d-none d-xl-block specific-left-padding-1'>
                  Help PolicyMogul improve briefings for you and others
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <CalendarPopup
        key={`dashboard-calendar-popup-shown-${calendarState.active}`}
        showPopup={calendarState.active}
        calendarState={calendarState}
        setCalendarState={setCalendarState}
        ApplyCalendarFilters={applyCalendarFilter}
        setShowPopup={(val) => setCalendarState({ ...calendarState, active: val })}
        resetOnApply={() => {
          hideFilters();
        }}
        allowedDates={Object.values(allowedDates).flat()}
        noFutureDatesAllowed={true}
        selectedMonth={dateProps || briefing?.date ? dayjs(dateProps ?? briefing?.date).toDate() : dayjs().toDate()}
        handleMonthChange={handleMonthChange}
      />
      <RefineBriefingModal
        setIsOpenDropdown={setIsOpen}
        isOpen={isRefineBriefingsOpen}
        setIsOpen={setIsRefineBriefingsOpen}
      />
    </>
  );
};

const SnapshotShareButton = (props) => {
  const { briefing, briefingContainer, updateState } = props;
  const { updatePanelState } = updateState ?? {};
  const [isOpen, setIsOpen] = useState(false);
  const actionsElement = useRef(null);
  const hideFilters = () => {
    setIsOpen(false);
  };
  useOutsideElement(actionsElement, hideFilters);
  const isOpenPanel = updatePanelState?.open;
  return (
    <div className='position-relative d-none d-xl-block' ref={actionsElement}>
      <button
        className={`general-button border rounded-pill flex-centered justify-content-center action-state-hover light-90-text  reset-link ${
          isOpenPanel ? 'circle-button circle-button-36 text-center mr-2' : 'px-4 specific-y-padding-5 mr-3'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        data-tooltip-content={isOpenPanel && !isOpen ? 'Share this briefing' : ''}
        data-tooltip-id='general-tooltip'
      >
        <span className={`icon-share ${isOpenPanel ? 'paragraph-p4 centered-2' : 'mr-2'}`} />
        {isOpenPanel ? '' : 'Share'}
      </button>
      {isOpen && (
        <div className='box-shadow py-2 position-absolute z-extra-max rounded' style={{ right: '0px', top: '39px' }}>
          <ShareBriefingActions briefing={briefing} hideFilters={hideFilters} briefingContainer={briefingContainer} />
        </div>
      )}
    </div>
  );
};

const ShareBriefingActions = (props) => {
  const { briefing, hideFilters, briefingContainer, fromPopup } = props ?? {};
  const embargoedCondition = briefing?.content?.some((item) => item?.itemContent?.some((item) => item?.embargoed));
  const [confirmed, setConfirmed] = useState(!embargoedCondition);
  return (
    <div style={{ minWidth: embargoedCondition ? '280px' : undefined }}>
      {embargoedCondition && (
        <div
          className={`border-bottom specific-left-padding-20 specific-right-padding-24 mb-2 ${
            fromPopup ? 'border-top specific-top-padding-16' : 'specific-top-padding-10'
          }`}
        >
          <p className='text-light-red mb-0'>
            This briefing references content that was under embargo when the briefing was created. Please be mindful of
            this if sharing the briefing externally
          </p>
          <div className='flex-grow-1 pt-1'>
            <div className={`checkbox-item d-flex`}>
              <input
                onChange={(e) => {
                  setConfirmed(e.target.checked);
                }}
                type={'checkbox'}
                checked={confirmed}
              />
              <label htmlFor={`I-understand`} className='create-email-alert-label'>
                <p className='mb-0 specific-top-margin-1'>I understand</p>
              </label>
            </div>
          </div>
        </div>
      )}
      <button
        className={`w-100 list-action-button py-2 general-button text-left nowrap-item pl-3 specific-right-padding-24 flex-centered flex-nowrap ${
          !confirmed ? 'opacity-5 pointer-event-none' : ''
        }`}
        onClick={() => {
          copyInnerHtml(briefingContainer?.current?.innerHTML, briefingContainer?.current?.innerText);
          if (hideFilters) {
            hideFilters();
          }
          //copyTextToClipboard(briefingContainer?.current?.innerText);
        }}
        disabled={!confirmed}
      >
        <div className={`icon-general-container text-center specific-right-margin-${fromPopup ? '12' : '7'}`}>
          <span className='icon-copy-thin title-h5-m ' />
        </div>
        <span>Copy text</span>
      </button>

      <Link
        to={{ pathname: `/reports/create` }}
        state={{ briefingDate: `${briefing.date}`, title: briefing?.title }}
        className={`w-100 list-action-button py-2 general-button text-left nowrap-item pl-3 specific-right-padding-24 flex-centered reset-link text-main-blue reset-link-color d-none d-lg-flex ${
          !confirmed ? 'opacity-5 pointer-event-none' : ''
        }`}
        onClick={(e) => {
          if (!confirmed) {
            e.preventDefault();
          }
        }}
      >
        <div className={`icon-general-container text-center specific-right-margin-${fromPopup ? '12' : '7'}`}>
          <span className='icon-document  paragraph-p1 d-inline-block-centered centered-6' />
        </div>
        <span>Create report</span>
      </Link>
    </div>
  );
};

export { SkeletonSnapshotComponent };
export default SnapshotComponent;
